import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheck, NotebookPen } from "lucide-react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import "./AssignAssessmentModal.css";
import { API_BASE_URL } from "./utils/apiBase";
import {
  MAX_ASSIGNMENT_MINUTES,
  MIN_ASSIGNMENT_MINUTES,
  assignmentTimingRequest,
  defaultAssignmentTiming,
  describeAssignmentSettings,
  serverAcceptedTiming,
} from "./utils/assignmentTiming";

const MAX_SELECTED_STUDENTS = 100;
const MAX_INSTRUCTIONS_LENGTH = 1000;
const ASSIGNMENT_MODES = {
  STUDENTS: "students",
  CLASSES: "classes",
};

const REJECTION_MESSAGES = {
  "student-not-connected": "This student is no longer connected to your account.",
  "already-completed": "This student has already completed this test.",
  "student-not-found": "The student account could not be found.",
  "target-is-not-a-student": "The selected account is not a student account.",
  "duplicate-student-uid": "This student was included more than once.",
  "work-type-conflict": "This student has already started this test as the other type of work, so it was not assigned. Duplicate the test to set it separately.",
};

// Homework starts with No timer. Once the teacher has chosen a time limit
// themselves, changing the work type never overrides that choice.
const initialTiming = (quiz, workType) => {
  const quizDefault = defaultAssignmentTiming(quiz);
  return workType === "homework" ? { ...quizDefault, mode: "untimed" } : quizDefault;
};

const studentDisplayName = (data = {}) =>
  data.displayName || data.username || data.email || "Unknown student";

const classDisplayName = (data = {}) => data.className || "Untitled Class";

const normalizedCount = (value) =>
  Number.isSafeInteger(value) && value >= 0 ? value : null;

const normalizeClassPreview = (data, selectedClassId) => {
  const countNames = [
    "activeMemberships",
    "uniqueStudents",
    "eligibleNew",
    "alreadyAssigned",
    "previouslyRevoked",
    "alreadyCompleted",
    "unavailable",
  ];
  if (
    data?.ok !== true ||
    data?.class?.id !== selectedClassId ||
    typeof data.class.name !== "string" ||
    !data.counts
  ) {
    return null;
  }

  const counts = {};
  for (const countName of countNames) {
    const count = normalizedCount(data.counts[countName]);
    if (count === null) return null;
    counts[countName] = count;
  }
  return {
    class: {
      id: data.class.id,
      name: data.class.name,
      code: typeof data.class.code === "string" ? data.class.code : "",
    },
    counts,
  };
};

const normalizeClassResult = (data, selectedClassId) => {
  const countNames = [
    "created",
    "alreadyAssigned",
    "previouslyRevoked",
    "alreadyCompleted",
    "unavailable",
  ];
  if (
    data?.ok !== true ||
    data?.mode !== "class" ||
    data?.class?.id !== selectedClassId ||
    typeof data.class.name !== "string" ||
    !data.counts
  ) {
    return null;
  }

  const counts = {};
  for (const countName of countNames) {
    const count = normalizedCount(data.counts[countName]);
    if (count === null) return null;
    counts[countName] = count;
  }
  // Reported only when some students had started the test as the other type.
  counts.workTypeConflict = normalizedCount(data.counts.workTypeConflict) ?? 0;
  return {
    mode: "class",
    class: {
      id: data.class.id,
      name: data.class.name,
      code: typeof data.class.code === "string" ? data.class.code : "",
    },
    counts,
  };
};

const pluralizeStudents = (count) => `${count} ${count === 1 ? "Student" : "Students"}`;

const focusableElements = (container) =>
  Array.from(
    container?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    ) || []
  ).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");

const AssignAssessmentModal = ({ quiz, onClose, onAssignmentResult, returnFocusTo, initialWorkType = "assessment" }) => {
  const startingWorkType = initialWorkType === "homework" ? "homework" : "assessment";
  const [assignmentMode, setAssignmentMode] = useState(ASSIGNMENT_MODES.STUDENTS);
  const [students, setStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [studentLoadError, setStudentLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudentUids, setSelectedStudentUids] = useState(() => new Set());
  const [selectionMessage, setSelectionMessage] = useState("");
  const [classes, setClasses] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [classLoadError, setClassLoadError] = useState("");
  const [classLoadAttempt, setClassLoadAttempt] = useState(0);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [classPreview, setClassPreview] = useState(null);
  const [isLoadingClassPreview, setIsLoadingClassPreview] = useState(false);
  const [classPreviewError, setClassPreviewError] = useState("");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [dueAt, setDueAt] = useState("");
  const [workType, setWorkType] = useState(startingWorkType);
  const [timing, setTiming] = useState(() => initialTiming(quiz, startingWorkType));
  const timingChosenRef = useRef(false);
  const [instructions, setInstructions] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const dialogRef = useRef(null);
  const searchInputRef = useRef(null);
  const resultDoneRef = useRef(null);
  const closeRef = useRef(onClose);
  const submittingRef = useRef(isSubmitting);
  const previewRequestIdRef = useRef(0);

  closeRef.current = onClose;
  submittingRef.current = isSubmitting;

  const quizTitle = quiz?.quizTitle || quiz?.title || quiz?.quizCode || "Untitled Test";

  useEffect(() => {
    previewRequestIdRef.current += 1;
    setSelectedClassId("");
    setClassPreview(null);
    setClassPreviewError("");
    setIsLoadingClassPreview(false);
    setResult(null);
    setWorkType(startingWorkType);
    setTiming(initialTiming(quiz, startingWorkType));
    timingChosenRef.current = false;
    // Only a different quiz resets the choice; edits to this one do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadConnectedStudents = async () => {
      setIsLoadingStudents(true);
      setStudentLoadError("");

      try {
        const currentUser = auth.currentUser;
        const teacherUid = String(currentUser?.uid || "").trim();
        if (!teacherUid) {
          throw new Error("Please sign in again before assigning an assessment.");
        }

        const teacherSnapshot = await getDoc(doc(db, "users", teacherUid));
        if (!teacherSnapshot.exists()) {
          throw new Error("Your teacher profile could not be found.");
        }

        const teacherCode = String(teacherSnapshot.data()?.teacherCode || "").trim();
        if (!teacherCode) {
          throw new Error("Your teacher code is missing. Please contact support before assigning assessments.");
        }

        const studentsQuery = query(
          collection(db, "users"),
          where("role", "==", "student"),
          where("teacherCodes", "array-contains", teacherCode)
        );
        const studentsSnapshot = await getDocs(studentsQuery);
        const loadedStudents = studentsSnapshot.docs
          .map((studentSnapshot) => {
            const data = studentSnapshot.data() || {};
            return {
              id: studentSnapshot.id,
              displayName: studentDisplayName(data),
              username: data.username || "",
              email: data.email || "",
            };
          })
          .sort((first, second) =>
            first.displayName.localeCompare(second.displayName, undefined, { sensitivity: "base" })
          );

        if (!cancelled) setStudents(loadedStudents);
      } catch (error) {
        if (!cancelled) {
          setStudents([]);
          setStudentLoadError(error.message || "Connected students could not be loaded.");
        }
      } finally {
        if (!cancelled) setIsLoadingStudents(false);
      }
    };

    loadConnectedStudents();
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  useEffect(() => {
    if (assignmentMode !== ASSIGNMENT_MODES.CLASSES) return undefined;
    let cancelled = false;

    const loadClasses = async () => {
      setIsLoadingClasses(true);
      setClassLoadError("");
      try {
        const teacherUid = String(auth.currentUser?.uid || "").trim();
        if (!teacherUid) {
          throw new Error("Please sign in again before assigning an assessment.");
        }
        const classesQuery = query(
          collection(db, "classes"),
          where("teacherUid", "==", teacherUid),
          where("active", "==", true)
        );
        const classesSnapshot = await getDocs(classesQuery);
        const loadedClasses = classesSnapshot.docs
          .map((classSnapshot) => {
            const data = classSnapshot.data() || {};
            return {
              id: classSnapshot.id,
              name: classDisplayName(data),
              code: typeof data.classCode === "string" ? data.classCode.trim() : "",
              active: data.active === true,
            };
          })
          .filter((classItem) => classItem.active)
          .sort((first, second) =>
            first.name.localeCompare(second.name, undefined, { sensitivity: "base" })
          );
        if (!cancelled) setClasses(loadedClasses);
      } catch (error) {
        if (!cancelled) {
          setClasses([]);
          setClassLoadError(
            error.message === "Please sign in again before assigning an assessment."
              ? error.message
              : "Your active classes could not be loaded. Check your connection and try again."
          );
        }
      } finally {
        if (!cancelled) setIsLoadingClasses(false);
      }
    };

    loadClasses();
    return () => {
      cancelled = true;
    };
  }, [assignmentMode, classLoadAttempt]);

  useEffect(() => {
    if (assignmentMode !== ASSIGNMENT_MODES.CLASSES || !selectedClassId) {
      previewRequestIdRef.current += 1;
      setClassPreview(null);
      setClassPreviewError("");
      setIsLoadingClassPreview(false);
      return undefined;
    }

    let cancelled = false;
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setClassPreview(null);
    setClassPreviewError("");
    setIsLoadingClassPreview(true);

    const loadPreview = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error("Please sign in again before previewing this class.");
        }
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/quiz-assignments/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ quizId: quiz.id, classId: selectedClassId }),
        });
        const responseData = await response.json().catch(() => ({}));
        if (!response.ok || responseData.ok !== true) {
          throw new Error(responseData.error || "The class preview could not be loaded.");
        }
        const normalizedPreview = normalizeClassPreview(responseData, selectedClassId);
        if (!normalizedPreview) {
          throw new Error("The class preview response was incomplete. Please try again.");
        }
        if (!cancelled && previewRequestIdRef.current === requestId) {
          setClassPreview(normalizedPreview);
        }
      } catch (error) {
        if (!cancelled && previewRequestIdRef.current === requestId) {
          setClassPreviewError(
            error.message || "The class preview could not be loaded. Please try again."
          );
        }
      } finally {
        if (!cancelled && previewRequestIdRef.current === requestId) {
          setIsLoadingClassPreview(false);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
      if (previewRequestIdRef.current === requestId) {
        previewRequestIdRef.current += 1;
      }
    };
  }, [assignmentMode, previewAttempt, quiz?.id, selectedClassId]);

  useEffect(() => {
    const previouslyFocusedElement =
      returnFocusTo instanceof HTMLElement ? returnFocusTo : document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (!submittingRef.current) {
          event.preventDefault();
          closeRef.current?.();
        }
        return;
      }

      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      const elements = focusableElements(dialog);
      if (!dialog || elements.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const activeElement = document.activeElement;
      if (event.shiftKey && (activeElement === first || !dialog.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      if (
        previouslyFocusedElement instanceof HTMLElement &&
        document.contains(previouslyFocusedElement)
      ) {
        previouslyFocusedElement.focus();
      }
    };
  }, [returnFocusTo]);

  useEffect(() => {
    if (!result) return undefined;
    const focusFrame = window.requestAnimationFrame(() => resultDoneRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [result]);

  const filteredStudents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return students;
    return students.filter((student) =>
      [student.displayName, student.username, student.email]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    );
  }, [searchTerm, students]);

  const studentByUid = useMemo(
    () => new Map(students.map((student) => [student.id, student])),
    [students]
  );

  const nameForUid = (studentUid) => studentByUid.get(studentUid)?.displayName || studentUid;

  const toggleStudent = (studentUid) => {
    setSelectionMessage("");
    setFormError("");

    if (selectedStudentUids.has(studentUid)) {
      setSelectedStudentUids((previous) => {
        const next = new Set(previous);
        next.delete(studentUid);
        return next;
      });
      return;
    }

    if (selectedStudentUids.size >= MAX_SELECTED_STUDENTS) {
      setSelectionMessage(`You can select a maximum of ${MAX_SELECTED_STUDENTS} students.`);
      return;
    }

    setSelectedStudentUids((previous) => new Set(previous).add(studentUid));
  };

  const selectVisibleStudents = () => {
    setSelectionMessage("");
    setFormError("");
    const newVisibleUids = filteredStudents
      .map((student) => student.id)
      .filter((studentUid) => !selectedStudentUids.has(studentUid));

    if (selectedStudentUids.size + newVisibleUids.length > MAX_SELECTED_STUDENTS) {
      setSelectionMessage(
        `Selecting these students would exceed the ${MAX_SELECTED_STUDENTS}-student limit. Narrow the search or clear some selections.`
      );
      return;
    }

    setSelectedStudentUids((previous) => {
      const next = new Set(previous);
      newVisibleUids.forEach((studentUid) => next.add(studentUid));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedStudentUids(new Set());
    setSelectionMessage("");
    setFormError("");
  };

  const changeAssignmentMode = (nextMode) => {
    if (nextMode === assignmentMode || isSubmitting) return;
    previewRequestIdRef.current += 1;
    setAssignmentMode(nextMode);
    setClassPreview(null);
    setClassPreviewError("");
    setIsLoadingClassPreview(false);
    setSelectionMessage("");
    setFormError("");
  };

  const selectClass = (classId) => {
    previewRequestIdRef.current += 1;
    setSelectedClassId(classId);
    setClassPreview(null);
    setClassPreviewError("");
    setFormError("");
  };

  const validateDueDate = () => {
    if (!dueAt) return { dueAtIso: null, error: "" };
    const parsedDueAt = new Date(dueAt);
    if (!Number.isFinite(parsedDueAt.getTime())) {
      return { dueAtIso: null, error: "Enter a valid due date and time." };
    }
    if (parsedDueAt.getTime() <= Date.now()) {
      return { dueAtIso: null, error: "The due date must be in the future." };
    }
    return { dueAtIso: parsedDueAt.toISOString(), error: "" };
  };

  const submitAssignment = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    setFormError("");
    setSelectionMessage("");
    if (
      assignmentMode === ASSIGNMENT_MODES.STUDENTS &&
      selectedStudentUids.size === 0
    ) {
      setFormError("Select at least one student before assigning this assessment.");
      return;
    }
    if (assignmentMode === ASSIGNMENT_MODES.CLASSES) {
      if (!selectedClassId) {
        setFormError("Select a class before assigning this assessment.");
        return;
      }
      if (!classPreview || classPreview.counts.eligibleNew === 0) {
        setFormError("There are no students currently available to assign in this class.");
        return;
      }
    }

    const timingRequest = assignmentTimingRequest(timing);
    if (timingRequest.error) {
      setFormError(timingRequest.error);
      return;
    }
    const settings = { workType, ...timingRequest.settings };

    const dueDateValidation = validateDueDate();
    if (dueDateValidation.error) {
      setFormError(dueDateValidation.error);
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before assigning this assessment.");
      }
      const token = await currentUser.getIdToken();
      const requestBody = assignmentMode === ASSIGNMENT_MODES.CLASSES
        ? {
            quizId: quiz.id,
            classId: selectedClassId,
            dueAt: dueDateValidation.dueAtIso,
            instructions,
            ...settings,
          }
        : {
            quizId: quiz.id,
            studentUids: Array.from(selectedStudentUids),
            dueAt: dueDateValidation.dueAtIso,
            instructions,
            ...settings,
          };
      const response = await fetch(`${API_BASE_URL}/api/quiz-assignments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok || responseData.ok !== true) {
        throw new Error(responseData.error || "The assessment could not be assigned. Please try again.");
      }

      let normalizedResult;
      if (assignmentMode === ASSIGNMENT_MODES.CLASSES) {
        normalizedResult = normalizeClassResult(responseData, selectedClassId);
        if (!normalizedResult) {
          throw new Error("The assignment response was incomplete. Please try again.");
        }
      } else {
        normalizedResult = {
          created: Array.isArray(responseData.created) ? responseData.created : [],
          alreadyAssigned: Array.isArray(responseData.alreadyAssigned)
            ? responseData.alreadyAssigned
            : [],
          rejected: Array.isArray(responseData.rejected) ? responseData.rejected : [],
        };
      }
      // The request has already been processed, so a mismatch is reported with
      // whatever was created rather than retried: resending cannot change
      // assignments that now exist.
      const timingResult = serverAcceptedTiming(settings, responseData.settings)
        ? { confirmed: true, settings }
        : {
          confirmed: false,
          settings,
          createdCount: normalizedResult.mode === "class"
            ? normalizedResult.counts.created
            : normalizedResult.created.length,
        };
      setResult({ ...normalizedResult, timing: timingResult });
      if (assignmentMode === ASSIGNMENT_MODES.CLASSES) {
        // Preserve ViewQuizzes' existing array-based lifecycle contract without
        // fabricating or exposing per-student data from the aggregate response.
        onAssignmentResult?.(quiz.id, {
          ...normalizedResult,
          created: normalizedResult.counts.created > 0 ? [{}] : [],
          alreadyAssigned: normalizedResult.counts.alreadyAssigned > 0 ? [{}] : [],
          rejected: [],
        });
      } else {
        onAssignmentResult?.(quiz.id, normalizedResult);
      }
    } catch (error) {
      setFormError(
        `${error.message || "The assessment could not be assigned."} Your selections have been kept so you can retry.`
      );
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const requestClose = () => {
    if (!isSubmitting) onClose?.();
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) requestClose();
  };

  const renderResultItems = (items, type) => {
    if (items.length === 0) return null;
    return (
      <ul className="assign-assessment-result-list">
        {items.map((item, index) => (
          <li key={`${item.studentUid}-${index}`}>
            <strong>{nameForUid(item.studentUid)}</strong>
            {type === "rejected" && (
              <span>{REJECTION_MESSAGES[item.reason] || "This student could not be assigned."}</span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  // Existing assignments are never changed, including their work type.
  const existingAssignmentNote = "Students who already had this test keep their existing assignment and work type. " +
    "To set it as a different type for them, duplicate the test and assign the copy.";

  // The class response reports only how many students were skipped, not who:
  // the count is stated exactly rather than looked up.
  const workTypeConflictNote = (count, requestedType = "assessment") => {
    const requested = requestedType === "homework" ? "homework" : "an assessment";
    const other = requestedType === "homework" ? "an assessment" : "homework";
    return `${count === 1 ? "1 student was" : `${count} students were`} not assigned because ` +
      `${count === 1 ? "they had" : "they had each"} already started this test as ${other}. ` +
      `Their existing attempt is unchanged. To set it for them as ${requested}, duplicate the test and assign the copy.`;
  };

  const chooseWorkType = (nextType) => {
    setWorkType(nextType);
    setFormError("");
    if (!timingChosenRef.current) setTiming(initialTiming(quiz, nextType));
  };

  const classSubmitLabel = (() => {
    if (isSubmitting) return "Assigning…";
    if (isLoadingClassPreview) return "Checking Class…";
    if (!selectedClassId || !classPreview) return "Select a Class";
    if (classPreview.counts.eligibleNew === 0) return "No Students to Assign";
    if (workType === "homework") return `Assign Homework to ${pluralizeStudents(classPreview.counts.eligibleNew)}`;
    return `Assign to ${pluralizeStudents(classPreview.counts.eligibleNew)}`;
  })();

  const isClassSubmitDisabled =
    isSubmitting ||
    isLoadingClasses ||
    isLoadingClassPreview ||
    Boolean(classLoadError) ||
    Boolean(classPreviewError) ||
    !selectedClassId ||
    !classPreview ||
    classPreview.counts.eligibleNew === 0;

  return (
    <div
      className="assign-assessment-backdrop"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className="assign-assessment-modal"
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-assessment-title"
        aria-describedby="assign-assessment-context"
        aria-busy={isSubmitting}
        tabIndex="-1"
      >
        <div className="assign-assessment-header">
          <div>
            <p className="assign-assessment-eyebrow">{workType === "homework" ? "Set as Homework" : "Assign Assessment"}</p>
            <h2 id="assign-assessment-title">{quizTitle}</h2>
            <p id="assign-assessment-context" className="assign-assessment-context">
              {quiz?.subject ? `${quiz.subject} · ` : ""}Choose students or one of your active classes.
            </p>
          </div>
          <button
            type="button"
            className="assign-assessment-close"
            onClick={requestClose}
            disabled={isSubmitting}
            aria-label="Close Assign Assessment"
          >
            ×
          </button>
        </div>

        {result ? (
          <div className="assign-assessment-results" role="status" aria-live="polite">
            {result.timing && (result.timing.confirmed ? (
              <p className="assign-assessment-result-timing">
                New assignments: <strong>{describeAssignmentSettings(result.timing.settings)}</strong>
              </p>
            ) : (
              <div className="assign-assessment-timing-warning" role="alert">
                <strong>StudyDen couldn't confirm the work type and time limit</strong>
                {result.timing.createdCount > 0 ? (
                  <p>
                    {result.timing.createdCount === 1 ? "1 assignment was" : `${result.timing.createdCount} assignments were`}{" "}
                    created, but the server didn't confirm the settings you chose
                    ({describeAssignmentSettings(result.timing.settings)}). These students may have been given
                    a timed Assessment using the test's own time limit instead. Assigning again won't change
                    existing assignments, so please don't resend. Ask your StudyDen administrator to check the
                    server is up to date before students start.
                  </p>
                ) : (
                  <p>
                    No new assignments were created, and the server didn't confirm the work type and time limit.
                    Check that StudyDen's server is up to date before assigning this test again.
                  </p>
                )}
              </div>
            ))}
            {result.mode === "class" ? (
              <>
                <div className="assign-assessment-class-result-heading">
                  <span>Class assignment complete</span>
                  <h3>{result.class.name}</h3>
                  {result.class.code && <p>{result.class.code}</p>}
                </div>
                <div className="assign-assessment-result-summary class-result">
                  <div className="assign-result-count created">
                    <strong>{result.counts.created}</strong>
                    <span>
                      {result.counts.created === 1
                        ? "student assigned successfully"
                        : "students assigned successfully"}
                    </span>
                  </div>
                  {result.counts.alreadyAssigned > 0 && (
                    <div className="assign-result-count existing">
                      <strong>{result.counts.alreadyAssigned}</strong>
                      <span>already assigned</span>
                    </div>
                  )}
                  {result.counts.previouslyRevoked > 0 && (
                    <div className="assign-result-count revoked">
                      <strong>{result.counts.previouslyRevoked}</strong>
                      <span>previously revoked — not reassigned</span>
                    </div>
                  )}
                  {result.counts.alreadyCompleted > 0 && (
                    <div className="assign-result-count completed">
                      <strong>{result.counts.alreadyCompleted}</strong>
                      <span>already completed</span>
                    </div>
                  )}
                  {result.counts.unavailable > 0 && (
                    <div className="assign-result-count unavailable">
                      <strong>{result.counts.unavailable}</strong>
                      <span>
                        {result.counts.unavailable === 1
                          ? "student record unavailable"
                          : "student records unavailable"}
                      </span>
                    </div>
                  )}
                  {result.counts.workTypeConflict > 0 && (
                    <div className="assign-result-count rejected">
                      <strong>{result.counts.workTypeConflict}</strong>
                      <span>already started as the other type of work — not assigned</span>
                    </div>
                  )}
                </div>
                {result.counts.alreadyAssigned > 0 && (
                  <p className="assign-assessment-result-note">{existingAssignmentNote}</p>
                )}
                {result.counts.workTypeConflict > 0 && (
                  <p className="assign-assessment-result-note assign-assessment-conflict-note">
                    {workTypeConflictNote(result.counts.workTypeConflict, result.timing?.settings?.workType)}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="assign-assessment-result-summary">
                  <div className="assign-result-count created">
                    <strong>{result.created.length}</strong>
                    <span>assigned successfully</span>
                  </div>
                  <div className="assign-result-count existing">
                    <strong>{result.alreadyAssigned.length}</strong>
                    <span>already assigned</span>
                  </div>
                  <div className="assign-result-count rejected">
                    <strong>{result.rejected.length}</strong>
                    <span>not assigned</span>
                  </div>
                </div>

                {result.created.length > 0 && (
                  <section className="assign-result-section created">
                    <h3>Assigned successfully</h3>
                    {renderResultItems(result.created, "created")}
                  </section>
                )}
                {result.alreadyAssigned.length > 0 && (
                  <section className="assign-result-section existing">
                    <h3>Already assigned</h3>
                    <p className="assign-assessment-result-note">{existingAssignmentNote}</p>
                    {renderResultItems(result.alreadyAssigned, "existing")}
                  </section>
                )}
                {result.rejected.length > 0 && (
                  <section className="assign-result-section rejected">
                    <h3>Not assigned</h3>
                    {renderResultItems(result.rejected, "rejected")}
                  </section>
                )}
              </>
            )}

            <div className="assign-assessment-actions">
              <button
                type="button"
                className="assign-assessment-primary"
                ref={resultDoneRef}
                onClick={requestClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitAssignment} noValidate>
            <fieldset className="assign-assessment-mode-fieldset">
              <legend>Assign to</legend>
                  <div
                    className="assign-assessment-mode-control"
                    role="group"
                    aria-label="Assignment target"
                  >
                <button
                  type="button"
                  aria-pressed={assignmentMode === ASSIGNMENT_MODES.STUDENTS}
                  className={assignmentMode === ASSIGNMENT_MODES.STUDENTS ? "active" : ""}
                  onClick={() => changeAssignmentMode(ASSIGNMENT_MODES.STUDENTS)}
                  disabled={isSubmitting}
                >
                  Students
                </button>
                <button
                  type="button"
                  aria-pressed={assignmentMode === ASSIGNMENT_MODES.CLASSES}
                  className={assignmentMode === ASSIGNMENT_MODES.CLASSES ? "active" : ""}
                  onClick={() => changeAssignmentMode(ASSIGNMENT_MODES.CLASSES)}
                  disabled={isSubmitting}
                >
                  Classes
                </button>
              </div>
            </fieldset>

            {assignmentMode === ASSIGNMENT_MODES.STUDENTS && (
            <section className="assign-assessment-section" aria-labelledby="assign-students-label">
              <div className="assign-assessment-section-heading">
                <div>
                  <h3 id="assign-students-label">Students</h3>
                  <p>{selectedStudentUids.size} of {MAX_SELECTED_STUDENTS} selected</p>
                </div>
                <div className="assign-assessment-selection-actions">
                  <button
                    type="button"
                    onClick={selectVisibleStudents}
                    disabled={isSubmitting || isLoadingStudents || filteredStudents.length === 0}
                  >
                    Select visible
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    disabled={isSubmitting || selectedStudentUids.size === 0}
                  >
                    Clear selection
                  </button>
                </div>
              </div>

              <label className="assign-assessment-search-label" htmlFor="assign-student-search">
                Search connected students
              </label>
              <input
                id="assign-student-search"
                ref={searchInputRef}
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name, username, or email"
                className="assign-assessment-search"
                disabled={isSubmitting}
                autoComplete="off"
              />

              {selectionMessage && (
                <div className="assign-assessment-inline-message" role="alert">
                  {selectionMessage}
                </div>
              )}

              <div className="assign-assessment-student-list" aria-live="polite">
                {isLoadingStudents ? (
                  <div className="assign-assessment-list-state">
                    <span className="assign-assessment-spinner" aria-hidden="true" />
                    Loading connected students…
                  </div>
                ) : studentLoadError ? (
                  <div className="assign-assessment-list-state error">
                    <p>{studentLoadError}</p>
                    <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                      Try again
                    </button>
                  </div>
                ) : students.length === 0 ? (
                  <div className="assign-assessment-list-state">
                    No connected students were found.
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="assign-assessment-list-state">
                    No students match this search.
                  </div>
                ) : (
                  filteredStudents.map((student) => (
                    <label className="assign-assessment-student" key={student.id}>
                      <input
                        type="checkbox"
                        checked={selectedStudentUids.has(student.id)}
                        onChange={() => toggleStudent(student.id)}
                        disabled={isSubmitting}
                      />
                      <span className="assign-assessment-checkbox" aria-hidden="true" />
                      <span className="assign-assessment-student-details">
                        <strong>{student.displayName}</strong>
                        {(student.email || student.username) && (
                          <span>
                            {[student.email, student.username].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </section>
            )}

            {assignmentMode === ASSIGNMENT_MODES.CLASSES && (
              <section className="assign-assessment-section" aria-labelledby="assign-classes-label">
                <div className="assign-assessment-section-heading">
                  <div>
                    <h3 id="assign-classes-label">Classes</h3>
                    <p>Select one active class</p>
                  </div>
                </div>

                <div
                  className="assign-assessment-class-list"
                  aria-busy={isLoadingClasses}
                  aria-live="polite"
                >
                  {isLoadingClasses ? (
                    <div className="assign-assessment-list-state">
                      <span className="assign-assessment-spinner" aria-hidden="true" />
                      Loading active classes…
                    </div>
                  ) : classLoadError ? (
                    <div className="assign-assessment-list-state error" role="alert">
                      <p>{classLoadError}</p>
                      <button
                        type="button"
                        onClick={() => setClassLoadAttempt((attempt) => attempt + 1)}
                      >
                        Try again
                      </button>
                    </div>
                  ) : classes.length === 0 ? (
                    <div className="assign-assessment-list-state assign-assessment-no-classes">
                      <strong>No active classes yet.</strong>
                      <span>You can create and manage classes from Classes &amp; Students.</span>
                    </div>
                  ) : (
                    classes.map((classItem) => (
                      <label
                        className={`assign-assessment-class${
                          selectedClassId === classItem.id ? " selected" : ""
                        }`}
                        key={classItem.id}
                      >
                        <input
                          type="radio"
                          name="assign-assessment-class"
                          value={classItem.id}
                          checked={selectedClassId === classItem.id}
                          onChange={() => selectClass(classItem.id)}
                          disabled={isSubmitting}
                        />
                        <span className="assign-assessment-radio" aria-hidden="true" />
                        <span className="assign-assessment-class-details">
                          <strong>{classItem.name}</strong>
                          {classItem.code && <span>{classItem.code}</span>}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {selectedClassId && (
                  <div
                    className="assign-assessment-preview-region"
                    aria-busy={isLoadingClassPreview}
                    aria-live="polite"
                  >
                    {isLoadingClassPreview ? (
                      <div className="assign-assessment-preview-state">
                        <span className="assign-assessment-spinner" aria-hidden="true" />
                        Checking current eligibility…
                      </div>
                    ) : classPreviewError ? (
                      <div className="assign-assessment-preview-error" role="alert">
                        <p>{classPreviewError}</p>
                        <button
                          type="button"
                          onClick={() => setPreviewAttempt((attempt) => attempt + 1)}
                        >
                          Try preview again
                        </button>
                      </div>
                    ) : classPreview ? (
                      <div className="assign-assessment-preview">
                        <div className="assign-assessment-preview-heading">
                          <div>
                            <h4>{classPreview.class.name}</h4>
                            {classPreview.class.code && <p>{classPreview.class.code}</p>}
                          </div>
                          <span>
                            {classPreview.counts.uniqueStudents}{" "}
                            {classPreview.counts.uniqueStudents === 1
                              ? "current student"
                              : "current students"}
                          </span>
                        </div>
                        <div className="assign-assessment-preview-primary">
                          <strong>{classPreview.counts.eligibleNew}</strong>
                          {" "}
                          <span>
                            {classPreview.counts.eligibleNew === 1
                              ? "student will receive this assessment"
                              : "students will receive this assessment"}
                          </span>
                        </div>
                        {classPreview.counts.eligibleNew === 0 && (
                          <p className="assign-assessment-preview-empty">
                            {classPreview.counts.uniqueStudents === 0
                              ? "This class has no students available for this assessment."
                              : "All available students are already assigned, completed, revoked, or unavailable."}
                          </p>
                        )}
                        <div className="assign-assessment-preview-counts">
                          {classPreview.counts.alreadyAssigned > 0 && (
                            <span className="existing">
                              <strong>{classPreview.counts.alreadyAssigned}</strong> already assigned
                            </span>
                          )}
                          {classPreview.counts.previouslyRevoked > 0 && (
                            <span className="revoked">
                              <strong>{classPreview.counts.previouslyRevoked}</strong>{" "}
                              previously revoked — not reassigned
                            </span>
                          )}
                          {classPreview.counts.alreadyCompleted > 0 && (
                            <span className="completed">
                              <strong>{classPreview.counts.alreadyCompleted}</strong> already completed
                            </span>
                          )}
                          {classPreview.counts.unavailable > 0 && (
                            <span className="unavailable">
                              <strong>{classPreview.counts.unavailable}</strong>{" "}
                              {classPreview.counts.unavailable === 1
                                ? "student record unavailable"
                                : "student records unavailable"}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </section>
            )}

            <div className="assign-assessment-fields">
              <fieldset className="assign-assessment-field assign-assessment-timing" disabled={isSubmitting}>
                <legend>Work type</legend>
                <div className="assign-assessment-timing-options">
                  {[["assessment", "Assessment", ClipboardCheck], ["homework", "Homework", NotebookPen]].map(([type, label, Icon]) => (
                    <label key={type} className={`assign-assessment-timing-option assign-work-type-${type}${workType === type ? " active" : ""}`}>
                      <input
                        type="radio"
                        name="assign-work-type"
                        value={type}
                        checked={workType === type}
                        onChange={() => chooseWorkType(type)}
                      />
                      <Icon size={15} aria-hidden="true" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <p className="assign-assessment-worktype-note">
                  {workType === "homework"
                    ? "Homework is labelled for students and reported separately from assessments."
                    : "Assessments are reported separately from homework."}
                </p>
              </fieldset>

              <fieldset className="assign-assessment-field assign-assessment-timing" disabled={isSubmitting}>
                <legend>Time limit</legend>
                <div className="assign-assessment-timing-options">
                  {[["timed", "Timed"], ["untimed", "No timer"]].map(([mode, label]) => (
                    <label key={mode} className={`assign-assessment-timing-option${timing.mode === mode ? " active" : ""}`}>
                      <input
                        type="radio"
                        name="assign-time-limit"
                        value={mode}
                        checked={timing.mode === mode}
                        onChange={() => {
                          timingChosenRef.current = true;
                          setTiming((current) => ({ ...current, mode }));
                          setFormError("");
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {timing.mode === "timed" ? (
                  <div className="assign-assessment-timing-duration">
                    <label htmlFor="assign-time-limit-minutes">
                      Duration <span>minutes, {MIN_ASSIGNMENT_MINUTES}–{MAX_ASSIGNMENT_MINUTES}</span>
                    </label>
                    <input
                      id="assign-time-limit-minutes"
                      type="number"
                      inputMode="numeric"
                      min={MIN_ASSIGNMENT_MINUTES}
                      max={MAX_ASSIGNMENT_MINUTES}
                      step="1"
                      value={timing.minutes}
                      onChange={(event) => {
                        timingChosenRef.current = true;
                        setTiming((current) => ({ ...current, minutes: event.target.value }));
                        setFormError("");
                      }}
                    />
                  </div>
                ) : (
                  <div className="assign-assessment-timing-notes" aria-live="polite">
                    <p>No countdown. Students can close StudyDen and carry on later; their answers save as they work.</p>
                    {!dueAt && (
                      <p className="assign-assessment-timing-open-note">
                        Without a due date, this work stays open until each student submits it.
                      </p>
                    )}
                  </div>
                )}
              </fieldset>

              <div className="assign-assessment-field">
                <label htmlFor="assign-due-at">
                  Due date and time <span>Optional</span>
                </label>
                <input
                  id="assign-due-at"
                  type="datetime-local"
                  value={dueAt}
                  onChange={(event) => {
                    setDueAt(event.target.value);
                    setFormError("");
                  }}
                  disabled={isSubmitting}
                />
              </div>

              <div className="assign-assessment-field">
                <div className="assign-assessment-label-row">
                  <label htmlFor="assign-instructions">
                    Instructions <span>Optional</span>
                  </label>
                  <span className="assign-assessment-character-count">
                    {instructions.length}/{MAX_INSTRUCTIONS_LENGTH}
                  </span>
                </div>
                <textarea
                  id="assign-instructions"
                  value={instructions}
                  onChange={(event) => setInstructions(event.target.value)}
                  maxLength={MAX_INSTRUCTIONS_LENGTH}
                  rows="3"
                  placeholder="Add any guidance for students"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="assign-assessment-error-region" aria-live="assertive">
              {formError && <div className="assign-assessment-form-error">{formError}</div>}
            </div>

            <div className="assign-assessment-actions">
              <button
                type="button"
                className="assign-assessment-secondary"
                onClick={requestClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="assign-assessment-primary"
                disabled={assignmentMode === ASSIGNMENT_MODES.CLASSES
                  ? isClassSubmitDisabled
                  : isSubmitting ||
                    isLoadingStudents ||
                    Boolean(studentLoadError) ||
                    selectedStudentUids.size === 0}
              >
                {assignmentMode === ASSIGNMENT_MODES.CLASSES
                  ? classSubmitLabel
                  : isSubmitting
                    ? "Assigning…"
                    : `Assign ${workType === "homework" ? "Homework" : "Assessment"}${selectedStudentUids.size ? ` (${selectedStudentUids.size})` : ""}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export {
  MAX_INSTRUCTIONS_LENGTH,
  MAX_SELECTED_STUDENTS,
  REJECTION_MESSAGES,
};

export default AssignAssessmentModal;
